import { WSQueries } from "../../../../query/WSQuery";
import { useQueryEngagementList } from "../../../../query/engagements/queries/useQueryEngagementList";
import {
  WSEmptyState,
  WSFilters,
  WSInfiniteScroll,
  WSPage,
  WSPageToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { EngagementRowsTable } from "../../components/EngagementRowsTable";
import { useBrowserPageTitle } from "../../../../shared/hooks/useBrowserPageTitle";
import { useCreateEngagementModal } from "../../modals/CreateEngagementModal";
import React from "react";
import { Tabs } from "../../../../components/Tabs";
import { getSectionPathByEngagementStatus } from "../../paths";
import { useQueryEngagementListSize } from "../../../../query/engagements/queries/useQueryEngagementListSize";
import { EngagementStatus } from "@wingspanhq/payments/dist/interfaces";
import { Route, Switch } from "react-router-dom";

const EngagementTypesTab: React.FC<{
  engagementStatus: EngagementStatus;
  isMainTab?: boolean;
}> = ({ engagementStatus, isMainTab }) => {
  const modalCreateEngagement = useCreateEngagementModal();
  const engagementFilter = {
    isDefault: false,
    status: engagementStatus
  };
  const queryEngagementList = useQueryEngagementList({
    filter: engagementFilter,
    sort: {
      // status: "asc",
      updatedAt: "desc"
    }
  });
  const queryEngagementListSize = useQueryEngagementListSize({
    filter: engagementFilter
  });

  return (
    <WSQueries
      queries={{
        queryEngagementList,
        queryEngagementListSize
      }}
    >
      {({ queryEngagementListData, queryEngagementListSizeData }) => {
        const list = queryEngagementListData.pages.flat();

        return (
          <>
            <WSFilters
              my="L"
              values={{}}
              onFilter={newValues => {}}
              info={{
                count: queryEngagementListSizeData ?? 0
              }}
              quickFilters={[]}
            />
            {list.length === 0 ? (
              <WSEmptyState
                py="3XL"
                hasBorder
                type="documents"
                title="No engagements yet"
                bgColor="gray"
                description={
                  isMainTab
                    ? "Once an engagement is added, it will appear here."
                    : undefined
                }
                actions={
                  isMainTab
                    ? {
                        buttons: [
                          {
                            label: "Add engagement",
                            kind: "Secondary",
                            onClick: async () => {
                              await modalCreateEngagement.open();
                            }
                          }
                        ]
                      }
                    : undefined
                }
              />
            ) : (
              <WSInfiniteScroll
                mt="M"
                onLoad={() => {
                  queryEngagementList.fetchNextPage();
                }}
                loadMore={list.length > 0}
                endOfList={!queryEngagementList.hasNextPage}
                loading={!!queryEngagementList.isFetchingNextPage}
              >
                <EngagementRowsTable engagements={list} />
              </WSInfiniteScroll>
            )}
          </>
        );
      }}
    </WSQueries>
  );
};

export const RouteEngagementTypes: React.FC = () => {
  useBrowserPageTitle("Engagements");

  const modalCreateEngagement = useCreateEngagementModal();

  const queryActiveEngagementTypeSummary = useQueryEngagementListSize({
    filter: {
      isDefault: false,
      status: EngagementStatus.Active
    }
  });

  const queryArchivedEngagementTypeSummary = useQueryEngagementListSize({
    filter: {
      isDefault: false,
      status: EngagementStatus.Inactive
    }
  });

  return (
    <WSPage>
      <WSPageToolbar
        title={"Engagements"}
        actions={[
          {
            label: "Add engagement",
            onClick: async () => {
              await modalCreateEngagement.open();
            },
            buttonKind: "Primary"
          }
        ]}
      >
        <WSText.ParagraphSm color="gray500" style={{ maxWidth: "550px" }}>
          Engagements represent a defined scope of work—like a project, or
          department—that can be assigned to contractors and can include
          specific requirements.
        </WSText.ParagraphSm>
      </WSPageToolbar>

      <WSQueries
        queries={{
          queryActiveEngagementTypeSummary,
          queryArchivedEngagementTypeSummary
        }}
      >
        {({
          queryActiveEngagementTypeSummaryData,
          queryArchivedEngagementTypeSummaryData
        }) => {
          const tabs = [
            {
              label: "Active",
              path: getSectionPathByEngagementStatus(EngagementStatus.Active),
              badge: queryActiveEngagementTypeSummaryData,
              engagementStatus: EngagementStatus.Active,
              isMainTab: true
            },
            {
              label: "Archived",
              path: getSectionPathByEngagementStatus(EngagementStatus.Inactive),
              badge: queryArchivedEngagementTypeSummaryData,
              engagementStatus: EngagementStatus.Inactive
            }
          ];

          return (
            <>
              <Tabs mb="L" tabs={tabs} />
              <Switch>
                {tabs.map(tab => (
                  <Route
                    key={tab.path}
                    path={tab.path}
                    render={() => (
                      <EngagementTypesTab
                        engagementStatus={tab.engagementStatus}
                        isMainTab={tab.isMainTab}
                      />
                    )}
                  />
                ))}
              </Switch>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
